import { AppThunk } from "../types";
import { getCallRoom, getDeviceStatus, getIpPhoneToken, getIsUpdatingSettings } from "./selectors";
import { CallRoom, DeviceStatus, IncomingCallStatus, IpPhoneSettings } from "./reducer";
import { postIpPhoneEventLog } from "../../api/ipPhone";
import dayjs from "dayjs";
import { sendMessage } from "../socket";
import { IP_PHONE_EVENT_TYPES } from "./middleware";
import { getCurrentWorkspaceId } from "../workspace";
import { Toast } from "../../components/antd/Toast";
import { intl } from "../../i18n";

export enum IpPhoneActions {
  SET_IS_PHONE_CALL = "ipPhone/SET_IS_PHONE_CALL",
  SET_CALL_SID = "ipPhone/SET_CALL_SID",
  UPDATE_INCOMING_CALL_STATUS = "ipPhone/UPDATE_INCOMING_CALL_STATUS",
  UPDATE_DEVICE_STATUS = "ipPhone/UPDATE_DEVICE_STATUS",
  SET_IP_PHONE_TOKEN = "ipPhone/SET_IP_PHONE_TOKEN",
  SET_CALL_ROOM = "ipPhone/SET_CALL_ROOM",
  SET_IP_PHONE_SETTINGS = "ipPhone/SET_IP_PHONE_SETTINGS",
  FETCH_IP_PHONE_SETTINGS = "ipPhone/FETCH_IP_PHONE_SETTINGS",
  SET_IS_UPDATING_SETTINGS = "ipPhone/SET_IS_UPDATING_SETTINGS",
  SET_MAKE_CALL_NUMBER = "ipPhone/SET_MAKE_CALL_NUMBER",
}

export type IpPhoneActionTypes =
  | ReturnType<typeof setIsPhoneCall>
  | ReturnType<typeof setCallSid>
  | ReturnType<typeof updateIncomingCallStatus>
  | ReturnType<typeof updateDeviceStatus>
  | ReturnType<typeof setIpPhoneToken>
  | ReturnType<typeof setCallRoom>
  | ReturnType<typeof setIpPhoneSettings>
  | ReturnType<typeof setIsUpdatingSettings>
  | ReturnType<typeof setMakeCallNumber>;

export function setIsPhoneCall(isPhoneCall: boolean) {
  return {
    type: IpPhoneActions.SET_IS_PHONE_CALL,
    payload: { isPhoneCall },
  } as const;
}

export function setCallSid(callSid: string | undefined) {
  return {
    type: IpPhoneActions.SET_CALL_SID,
    payload: { callSid },
  } as const;
}

export function updateIncomingCallStatus(incomingCallStatus: IncomingCallStatus) {
  return {
    type: IpPhoneActions.UPDATE_INCOMING_CALL_STATUS,
    payload: { incomingCallStatus },
  } as const;
}

export function updateDeviceStatus(deviceStatus: DeviceStatus | null) {
  return {
    type: IpPhoneActions.UPDATE_DEVICE_STATUS,
    payload: { deviceStatus },
  } as const;
}

export function setIpPhoneToken(ipPhoneToken: string) {
  return {
    type: IpPhoneActions.SET_IP_PHONE_TOKEN,
    payload: { ipPhoneToken },
  } as const;
}

export function sendPhoneCallLogs({
  eventType,
  identity,
  roomName,
  description,
}: {
  eventType: string;
  identity?: string;
  roomName?: string;
  description?: any;
}): AppThunk {
  return (dispatch, getState) => {
    const deviceStatus = getDeviceStatus(getState());
    const token = getIpPhoneToken(getState());
    const callRoom = getCallRoom(getState());

    if (!deviceStatus || !token) {
      return;
    }

    postIpPhoneEventLog(
      {
        event_type: eventType,
        identity: identity || callRoom?.identity || "",
        room_name: roomName || callRoom?.roomName || "",
        description,
        date: dayjs().format("YYYY-MM-DD HH:mm:ss.SSS Z"),
      },
      token,
    )
      .then(response => {
        console.log("Successfully sent logs", response);
      })
      .catch(e => {
        console.log("Failed to send logs", e);
      });
  };
}

export function setCallRoom(callRoom: CallRoom) {
  return {
    type: IpPhoneActions.SET_CALL_ROOM,
    payload: { callRoom },
  } as const;
}

export function setIpPhoneSettings(settings: IpPhoneSettings) {
  return {
    type: IpPhoneActions.SET_IP_PHONE_SETTINGS,
    payload: { settings },
  } as const;
}

export function updatedIpPhoneSettings(settings: IpPhoneSettings): AppThunk {
  return (dispatch, getState) => {
    const isUpdatingSettings = getIsUpdatingSettings(getState());
    if (isUpdatingSettings) {
      Toast.success(
        intl.formatMessage({
          id: "settings-ip-phone/settings-updating",
          defaultMessage: "Updated settings successfully",
        }),
      );
    }
    dispatch(setIpPhoneSettings(settings));
    dispatch(setIsUpdatingSettings(false));
  };
}

export function updateIpPhoneSettings(settings: IpPhoneSettings): AppThunk {
  return (dispatch, getState) => {
    const workspaceId = getCurrentWorkspaceId(getState());
    dispatch(sendMessage(IP_PHONE_EVENT_TYPES.IP_PHONE_SETTINGS_UPDATE_REQUEST, { workspaceId, settings }));
    dispatch(setIsUpdatingSettings(true));
  };
}

export function setIsUpdatingSettings(isUpdatingSettings: boolean) {
  return {
    type: IpPhoneActions.SET_IS_UPDATING_SETTINGS,
    payload: { isUpdatingSettings },
  } as const;
}

export function setMakeCallNumber(makeCallNumber?: string) {
  return {
    type: IpPhoneActions.SET_MAKE_CALL_NUMBER,
    payload: { makeCallNumber },
  } as const;
}
